import {Observable, of} from "rxjs";
import {ofType, StateObservable} from "redux-observable";
import {AppState} from "../../reducers/RootReducer";
import {catchError, map, mergeMap, switchMap} from "rxjs/operators";
import {ApiReturnTotalsType} from "../../models/common/ApiReturnType";
import {ajax} from "rxjs/ajax";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {eventNotificationError} from "../common/EventNotificationActions";
import {Nullable} from "../../models/common/Nullable";
import {getInitialViewSettings, UserViewSettings, UserViewSettingsProp} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {BillSummable, OutgoingInvoiceModule} from "../../models/accounting/AccountBook";
import {OutgoingInvoice, OutgoingInvoiceId, OutgoingInvoiceProp} from "../../models/accounting/OutgoingInvoice";
import {
    getUrlParamsFromViewSettings,
    multipartHeaders,
    RouteConfig,
    RouteMethods,
    toFormDataEncode
} from "../../config/RouteConfig";
import {Identifier, makeNumericId} from "../../models/common/Identifier";
import {Sensitive} from "../../models/common/Sensitive";
import {Money} from "../../models/accounting/Finance";
import {ProjectId} from "../../models/projects/Project";
import {InvoiceType} from "../../models/accounting/InvoiceType";
import {sendUserToPage} from "../common/UserViewSettingsActions";
import {FileData} from "../../models/common/FileData";
import {
    DeleteOutgoingInvoicePaymentActionTypes,
    InsertOutgoingInvoicePaymentActionTypes
} from "./OutgoingInvoicePaymentActions";
import {dateAsTimestamp} from "../../helpers/DateTimeHelpers";
import {isInvoiceProjectTaskFlatRateItem} from "../../models/accounting/OutgoingInvoiceProjectTaskItem";
import {isInvoiceProjectTaskTimeItem} from "../../models/accounting/OutgoingInvoiceProjectTaskTimeItem";
import {InvoiceMethod} from "../../models/accounting/InvoiceMethod";
import {isSimpleOutgoingInvoiceItem} from "../../models/accounting/OutgoingInvoiceItem";


/** backend api path */
function apiPath( id: Nullable< number > = null ): string {
    const uri = 'invoices/outgoing/'
    if( id === null ) {
        return APP_CONFIG.baseWeb + uri;
    } else {
        return APP_CONFIG.baseWeb + uri  + id;
    }
}


/** union of all Bill related action types */
export type OutgoingInvoiceViewActionTypes = FetchOutgoingInvoicesActionTypes
    | FetchOutgoingInvoicesNextSerialActionTypes
    | FetchOutgoingInvoicesPrePaymentByProjectActionTypes
    | CreateOutgoingInvoiceActionTypes
    | UpdateOutgoingInvoiceActionTypes
    | FetchOutgoingInvoicesCheckFinalActionTypes
    | FetchOutgoingInvoiceByIdActionTypes
    | MarkCanceledOutgoingInvoiceActionTypes
    | MarkFinalizedOutgoingInvoiceActionTypes
    | InsertOutgoingInvoicePaymentActionTypes
    | DeleteOutgoingInvoicePaymentActionTypes
    | FetchOutInvoiceFilesByIdActionTypes
    | DeleteOutgoingInvoiceByIdActionTypes
    


/**
 * fetch list of bills action types
 */
export interface FetchOutgoingInvoicesAction extends UserViewSettingsProp, ReduxAction< typeof FETCH_OUTGOING_INVOICES > {}
export interface FetchOutgoingInvoicesSuccessAction extends ReduxAction<typeof FETCH_OUTGOING_INVOICES_SUCCESS > {
    payload: ApiReturnTotalsType< OutgoingInvoice[], BillSummable >
    
}
export interface FetchOutgoingInvoicesErrorAction extends  ErrorAction< typeof FETCH_OUTGOING_INVOICES_ERROR >{}

export type FetchOutgoingInvoicesActionTypes = FetchOutgoingInvoicesAction | FetchOutgoingInvoicesErrorAction | FetchOutgoingInvoicesSuccessAction

/**
 * redux action constants
 */
export const FETCH_OUTGOING_INVOICES = "FETCH_OUTGOING_INVOICES";
export const FETCH_OUTGOING_INVOICES_SUCCESS = "FETCH_OUTGOING_INVOICES_SUCCESS";
export const FETCH_OUTGOING_INVOICES_ERROR = "FETCH_OUTGOING_INVOICES_ERROR";

/**
 * redux action functions
 */
export function fetchOutgoingInvoices( userViewSettings: UserViewSettings ) : FetchOutgoingInvoicesAction {
    return { type: FETCH_OUTGOING_INVOICES, userViewSettings };
}
export function fetchOutgoingInvoicesSuccess( payload: ApiReturnTotalsType< OutgoingInvoice[], BillSummable >) : FetchOutgoingInvoicesSuccessAction {
    return { type: FETCH_OUTGOING_INVOICES_SUCCESS, payload };
}
export function fetchOutgoingInvoicesError( error: Error ): FetchOutgoingInvoicesErrorAction {
    return { type: FETCH_OUTGOING_INVOICES_ERROR, error };
}

/**
 * redux observable to fetch bills from api
 */
export function fetchOutgoingInvoicesEpic(
    action$: Observable< FetchOutgoingInvoicesAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_OUTGOING_INVOICES),
        switchMap(action => {
            const path = `${ apiPath() }${  getUrlParamsFromViewSettings( action.userViewSettings ) }`;

            return ajax(
                APP_CONFIG.getRequestObject( path )
            ).pipe(
                map(response => fetchOutgoingInvoicesSuccess(
                    response.response as ApiReturnTotalsType< OutgoingInvoice[], BillSummable >
                )),
                catchError(error => of(
                    fetchOutgoingInvoicesError( error),
                    eventNotificationError(error, store$, OutgoingInvoiceModule.identifier, "fetchOutgoingInvoices")
                ))
            );

        })
    );
}



/**
 * fetch list of bills action types
 */
export interface FetchOutgoingInvoicesNextSerialAction extends  ReduxAction< typeof FETCH_OUTGOING_INVOICES_NEXT_SERIAL > {
    year: number
}
export interface FetchOutgoingInvoicesNextSerialSuccessAction extends ReduxAction<typeof FETCH_OUTGOING_INVOICES_NEXT_SERIAL_SUCCESS > {
    payload: number

}
export interface FetchOutgoingInvoicesNextSerialErrorAction extends  ErrorAction< typeof FETCH_OUTGOING_INVOICES_NEXT_SERIAL_ERROR >{}

export type FetchOutgoingInvoicesNextSerialActionTypes = FetchOutgoingInvoicesNextSerialAction | FetchOutgoingInvoicesNextSerialErrorAction | FetchOutgoingInvoicesNextSerialSuccessAction

/**
 * redux action constants
 */
export const FETCH_OUTGOING_INVOICES_NEXT_SERIAL = "FETCH_OUTGOING_INVOICES_NEXT_SERIAL";
export const FETCH_OUTGOING_INVOICES_NEXT_SERIAL_SUCCESS = "FETCH_OUTGOING_INVOICES_NEXT_SERIAL_SUCCESS";
export const FETCH_OUTGOING_INVOICES_NEXT_SERIAL_ERROR = "FETCH_OUTGOING_INVOICES_NEXT_SERIAL_ERROR";

/**
 * redux action functions
 */
export function fetchOutgoingInvoicesNextSerial( year: number ) : FetchOutgoingInvoicesNextSerialAction {
    return { type: FETCH_OUTGOING_INVOICES_NEXT_SERIAL, year };
}
export function fetchOutgoingInvoicesNextSerialSuccess( payload: number ) : FetchOutgoingInvoicesNextSerialSuccessAction {
    return { type: FETCH_OUTGOING_INVOICES_NEXT_SERIAL_SUCCESS, payload };
}
export function fetchOutgoingInvoicesNextSerialError( error: Error ): FetchOutgoingInvoicesNextSerialErrorAction {
    return { type: FETCH_OUTGOING_INVOICES_NEXT_SERIAL_ERROR, error };
}

/**
 * redux observable to fetch bills from api
 */
export function fetchOutgoingInvoicesNextSerialEpic(
    action$: Observable< FetchOutgoingInvoicesNextSerialAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_OUTGOING_INVOICES_NEXT_SERIAL),
        switchMap(action => {
            const path = `${ apiPath() }next-serial/${ action.year }`;

            return ajax(
                APP_CONFIG.getRequestObject( path )
            ).pipe(
                map(response => fetchOutgoingInvoicesNextSerialSuccess(
                    response.response as number
                )),
                catchError(error => of(
                    fetchOutgoingInvoicesNextSerialError( error),
                    eventNotificationError(error, store$, OutgoingInvoiceModule.identifier, "fetchOutgoingInvoicesNextSerial")
                ))
            );

        })
    );
}

/**
 * fetch pre payments by project
 */
export interface FetchOutgoingInvoicesPrePaymentByProjectAction extends  ReduxAction< typeof FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT > {
    projectId: Identifier<number>
}
export interface FetchOutgoingInvoicesPrePaymentByProjectSuccessAction extends ReduxAction<typeof FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT_SUCCESS > {
    payload: Sensitive<Money>

}
export interface FetchOutgoingInvoicesPrePaymentByProjectErrorAction extends  ErrorAction< typeof FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT_ERROR >{}

export type FetchOutgoingInvoicesPrePaymentByProjectActionTypes = FetchOutgoingInvoicesPrePaymentByProjectAction | FetchOutgoingInvoicesPrePaymentByProjectErrorAction | FetchOutgoingInvoicesPrePaymentByProjectSuccessAction

/**
 * redux action constants
 */
export const FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT = "FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT";
export const FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT_SUCCESS = "FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT_SUCCESS";
export const FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT_ERROR = "FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT_ERROR";

/**
 * redux action functions
 */
export function fetchOutgoingInvoicesPrePaymentByProject( projectId: Identifier<number> ) : FetchOutgoingInvoicesPrePaymentByProjectAction {
    return { type: FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT, projectId };
}
export function fetchOutgoingInvoicesPrePaymentByProjectSuccess( payload: Sensitive< Money > ) : FetchOutgoingInvoicesPrePaymentByProjectSuccessAction {
    return { type: FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT_SUCCESS, payload };
}
export function fetchOutgoingInvoicesPrePaymentByProjectError( error: Error ): FetchOutgoingInvoicesPrePaymentByProjectErrorAction {
    return { type: FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT_ERROR, error };
}

/**
 * redux observable to fetch bills from api
 */
export function fetchOutgoingInvoicesPrePaymentByProjectEpic(
    action$: Observable< FetchOutgoingInvoicesPrePaymentByProjectAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT),
        switchMap(action => {
            const path = `${ apiPath() }projects/${ action.projectId.rawValue }/payments`;

            return ajax(
                APP_CONFIG.getRequestObject( path )
            ).pipe(
                map(response => fetchOutgoingInvoicesPrePaymentByProjectSuccess(
                    response.response as Sensitive< Money >
                )),
                catchError(error => of(
                    fetchOutgoingInvoicesPrePaymentByProjectError( error),
                    eventNotificationError(error, store$, OutgoingInvoiceModule.identifier, "fetchOutgoingInvoicesPrePaymentByProject")
                ))
            );

        })
    );
}



/**
 * check if the project already has a final invoice 
 */
export interface FetchOutgoingInvoicesCheckFinalAction extends ProjectId, ReduxAction< typeof FETCH_OUTGOING_INVOICES_CHECK_FINAL > {
}
export interface FetchOutgoingInvoicesCheckFinalSuccessAction extends ReduxAction<typeof FETCH_OUTGOING_INVOICES_CHECK_FINAL_SUCCESS > {
    payload: number
}
export interface FetchOutgoingInvoicesCheckFinalErrorAction extends  ErrorAction< typeof FETCH_OUTGOING_INVOICES_CHECK_FINAL_ERROR >{}

export type FetchOutgoingInvoicesCheckFinalActionTypes = FetchOutgoingInvoicesCheckFinalAction | FetchOutgoingInvoicesCheckFinalErrorAction | FetchOutgoingInvoicesCheckFinalSuccessAction

/**
 * redux action constants
 */
export const FETCH_OUTGOING_INVOICES_CHECK_FINAL = "FETCH_OUTGOING_INVOICES_CHECK_FINAL";
export const FETCH_OUTGOING_INVOICES_CHECK_FINAL_SUCCESS = "FETCH_OUTGOING_INVOICES_CHECK_FINAL_SUCCESS";
export const FETCH_OUTGOING_INVOICES_CHECK_FINAL_ERROR = "FETCH_OUTGOING_INVOICES_CHECK_FINAL_ERROR";

/**
 * redux action functions
 */
export function fetchOutgoingInvoicesCheckFinal( projectId: Identifier< number > ) : FetchOutgoingInvoicesCheckFinalAction {
    return { type: FETCH_OUTGOING_INVOICES_CHECK_FINAL, projectId };
}
export function fetchOutgoingInvoicesCheckFinalSuccess( payload: number ) : FetchOutgoingInvoicesCheckFinalSuccessAction {
    return { type: FETCH_OUTGOING_INVOICES_CHECK_FINAL_SUCCESS, payload };
}
export function fetchOutgoingInvoicesCheckFinalError( error: Error ): FetchOutgoingInvoicesCheckFinalErrorAction {
    return { type: FETCH_OUTGOING_INVOICES_CHECK_FINAL_ERROR, error };
}

/**
 * redux observable to fetch bills from api
 */
export function fetchOutgoingInvoicesCheckFinalEpic(
    action$: Observable< FetchOutgoingInvoicesCheckFinalAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_OUTGOING_INVOICES_CHECK_FINAL),
        switchMap(action => {
            const path = `${ apiPath() }check-final/${ action.projectId.rawValue }`;

            return ajax(
                APP_CONFIG.getRequestObject( path )
            ).pipe(
                map(response => fetchOutgoingInvoicesCheckFinalSuccess(
                    response.response as number
                )),
                catchError(error => of(
                    fetchOutgoingInvoicesCheckFinalError( error),
                    eventNotificationError(error, store$, OutgoingInvoiceModule.identifier, "fetchOutgoingInvoicesCheckFinal")
                ))
            );

        })
    );
}

/**
 * CREATE OUTGOING INVOICE 
 */
export interface CreateOutgoingInvoiceAction extends OutgoingInvoiceProp, ReduxAction< typeof CREATE_OUTGOING_INVOICE > {
    files: File[]
}
export interface CreateOutgoingInvoiceSuccessAction extends ReduxAction<typeof CREATE_OUTGOING_INVOICE_SUCCESS > {

}
export interface CreateOutgoingInvoiceErrorAction extends  ErrorAction< typeof CREATE_OUTGOING_INVOICE_ERROR >{}

export type CreateOutgoingInvoiceActionTypes = CreateOutgoingInvoiceAction | CreateOutgoingInvoiceErrorAction | CreateOutgoingInvoiceSuccessAction

/**
 * redux action constants
 */
export const CREATE_OUTGOING_INVOICE = "CREATE_OUTGOING_INVOICE";
export const CREATE_OUTGOING_INVOICE_SUCCESS = "CREATE_OUTGOING_INVOICE_SUCCESS";
export const CREATE_OUTGOING_INVOICE_ERROR = "CREATE_OUTGOING_INVOICE_ERROR";

/**
 * redux action functions
 */
export function createOutgoingInvoice(
    outgoingInvoice: OutgoingInvoice,
    files: File[]
) : CreateOutgoingInvoiceAction {
    return { type: CREATE_OUTGOING_INVOICE, outgoingInvoice, files };
}
export function createOutgoingInvoiceSuccess() : CreateOutgoingInvoiceSuccessAction {
    return { type: CREATE_OUTGOING_INVOICE_SUCCESS };
}
export function createOutgoingInvoiceError( error: Error ): CreateOutgoingInvoiceErrorAction {
    return { type: CREATE_OUTGOING_INVOICE_ERROR, error };
}

/**
 * redux observable to create bills from api
 */
export function createOutgoingInvoiceEpic(
    action$: Observable< CreateOutgoingInvoiceAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(CREATE_OUTGOING_INVOICE),
        switchMap(action => {
            const path = apiPath();

            let reqObj = {
                ...action.outgoingInvoice,
                invoiceDate: action.outgoingInvoice.invoiceDate.getTime(),
                period: {
                    startAt: action.outgoingInvoice.period.startAt.getTime(),
                    finishAt: action.outgoingInvoice.period.finishAt.getTime(),
                },
                payableState: {
                    ...action.outgoingInvoice.payableState,
                    paymentExpected: action.outgoingInvoice.payableState.paymentExpected.getTime()
                },
                idOnReq: action.outgoingInvoice.outgoingInvoiceId.rawValue
            };

            if( action.outgoingInvoice.type === InvoiceType.SINGLE_INVOICE ) {
                //@ts-ignore
                reqObj = action.outgoingInvoice.method === InvoiceMethod.SIMPLE
                ? {
                    ...reqObj,
                        items: [],
                        //@ts-ignore
                        timeItems: [],
                        basicItems: action.outgoingInvoice.items.filter( _item => isSimpleOutgoingInvoiceItem( _item ) )

                    }
                : {
                    ...reqObj,
                    basicItems: [],
                    items: action.outgoingInvoice.items.filter( _item => isInvoiceProjectTaskFlatRateItem( _item ) ),
                    //@ts-ignore
                    timeItems: action.outgoingInvoice.items.filter( _item => isInvoiceProjectTaskTimeItem( _item ) )
                };
            }


            // @ts-ignore
            delete reqObj.outgoingInvoiceId;
            // @ts-ignore
//            delete reqObj.stages;
            //@ts-ignore
            delete reqObj.totals;
            //@ts-ignore
            delete reqObj.invoiceNumber;

            return ajax({
                ...APP_CONFIG.getRequestObject(
                    path,
                    RouteMethods.post,
                    toFormDataEncode('invoice', reqObj, action.files),
                    multipartHeaders()
                ),
                timeout: 7000
            }).pipe(
                mergeMap( _ => of(
                    createOutgoingInvoiceSuccess(),
                    fetchOutgoingInvoices( getInitialViewSettings( OutgoingInvoiceModule.identifier ) ),
                    sendUserToPage(
                        RouteConfig.accountBook.routes.main.path,
                        OutgoingInvoiceModule.identifier
                    )
                )),
                catchError(error => of(
                    createOutgoingInvoiceError( error),
                    eventNotificationError(error, store$, OutgoingInvoiceModule.identifier, "createOutgoingInvoice")
                ))
            );

        })
    );
}

/**
 * UPDATE OUTGOING INVOICE
 */
export interface UpdateOutgoingInvoiceAction extends OutgoingInvoiceProp, ReduxAction< typeof UPDATE_OUTGOING_INVOICE > {
    files: File[]
}
export interface UpdateOutgoingInvoiceSuccessAction extends ReduxAction<typeof UPDATE_OUTGOING_INVOICE_SUCCESS > {

}
export interface UpdateOutgoingInvoiceErrorAction extends  ErrorAction< typeof UPDATE_OUTGOING_INVOICE_ERROR >{}

export type UpdateOutgoingInvoiceActionTypes = UpdateOutgoingInvoiceAction | UpdateOutgoingInvoiceErrorAction | UpdateOutgoingInvoiceSuccessAction

/**
 * redux action constants
 */
export const UPDATE_OUTGOING_INVOICE = "UPDATE_OUTGOING_INVOICE";
export const UPDATE_OUTGOING_INVOICE_SUCCESS = "UPDATE_OUTGOING_INVOICE_SUCCESS";
export const UPDATE_OUTGOING_INVOICE_ERROR = "UPDATE_OUTGOING_INVOICE_ERROR";

/**
 * redux action functions
 */
export function updateOutgoingInvoice(
    outgoingInvoice: OutgoingInvoice,
    files: File[]
) : UpdateOutgoingInvoiceAction {
    return { type: UPDATE_OUTGOING_INVOICE, outgoingInvoice, files };
}
export function updateOutgoingInvoiceSuccess() : UpdateOutgoingInvoiceSuccessAction {
    return { type: UPDATE_OUTGOING_INVOICE_SUCCESS };
}
export function updateOutgoingInvoiceError( error: Error ): UpdateOutgoingInvoiceErrorAction {
    return { type: UPDATE_OUTGOING_INVOICE_ERROR, error };
}

/**
 * redux observable to update bills from api
 */
export function updateOutgoingInvoiceEpic(
    action$: Observable< UpdateOutgoingInvoiceAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(UPDATE_OUTGOING_INVOICE),
        switchMap(action => {
            const path = apiPath() +"update";

            let reqObj = {
                ...action.outgoingInvoice,
                invoiceDate: dateAsTimestamp( action.outgoingInvoice.invoiceDate ),
                period: {
                    startAt: dateAsTimestamp( action.outgoingInvoice.period.startAt ),
                    finishAt: dateAsTimestamp( action.outgoingInvoice.period.finishAt ),
                },
                payableState: {
                    ...action.outgoingInvoice.payableState,
                    paymentExpected: dateAsTimestamp( action.outgoingInvoice.payableState.paymentExpected )
                },
                idOnReq: action.outgoingInvoice.outgoingInvoiceId.rawValue
            };


            if( action.outgoingInvoice.type === InvoiceType.SINGLE_INVOICE ) {
                //@ts-ignore
                reqObj = action.outgoingInvoice.method === InvoiceMethod.SIMPLE
                    ? {
                        ...reqObj,
                        items: [],
                        //@ts-ignore
                        timeItems: [],
                        basicItems: action.outgoingInvoice.items.filter( _item =>
                            isSimpleOutgoingInvoiceItem( _item )
                        )
                    }
                    : {
                        ...reqObj,
                        basicItems: [],
                        items: action.outgoingInvoice.items.filter( _item => isInvoiceProjectTaskFlatRateItem( _item ) ),
                        //@ts-ignore
                        timeItems: action.outgoingInvoice.items.filter( _item => isInvoiceProjectTaskTimeItem( _item ) )
                    };
            }


            // @ts-ignore
            delete reqObj.outgoingInvoiceId;
            // @ts-ignore
//            delete reqObj.stages;
            //@ts-ignore
            delete reqObj.totals;
            //@ts-ignore
            delete reqObj.invoiceNumber;

            return ajax(
                {
                    ...APP_CONFIG.getRequestObject(
                        path,
                        RouteMethods.put,
                        toFormDataEncode('invoice', reqObj, action.files),
                        multipartHeaders()
                    ),
                    timeout: 7000
                }
            ).pipe(
                mergeMap( _ => of(
                    updateOutgoingInvoiceSuccess(),
                    fetchOutgoingInvoiceById( action.outgoingInvoice.outgoingInvoiceId ),
                    fetchOutgoingInvoices( getInitialViewSettings( OutgoingInvoiceModule.identifier ) ),
                    sendUserToPage(
                        RouteConfig.accountBook.routes.main.path,
                        OutgoingInvoiceModule.identifier
                    )
                )),
                catchError(error => of(
                    updateOutgoingInvoiceError( error),
                    eventNotificationError(error, store$, OutgoingInvoiceModule.identifier, "updateOutgoingInvoice")
                ))
            );

        })
    );
}

/**
 * FETCH DETAIL INVOICE
 */
export interface FetchOutgoingInvoiceByIdAction extends OutgoingInvoiceId, ReduxAction< typeof FETCH_OUTGOING_INVOICE_BY_ID > {

}
export interface FetchOutgoingInvoiceByIdSuccessAction extends ReduxAction<typeof FETCH_OUTGOING_INVOICE_BY_ID_SUCCESS > {
    payload: OutgoingInvoice

}
export interface FetchOutgoingInvoiceByIdErrorAction extends OutgoingInvoiceId, ErrorAction< typeof FETCH_OUTGOING_INVOICE_BY_ID_ERROR >{}

export type FetchOutgoingInvoiceByIdActionTypes = FetchOutgoingInvoiceByIdAction | FetchOutgoingInvoiceByIdErrorAction | FetchOutgoingInvoiceByIdSuccessAction

/**
 * redux action constants
 */
export const FETCH_OUTGOING_INVOICE_BY_ID = "FETCH_OUTGOING_INVOICE_BY_ID";
export const FETCH_OUTGOING_INVOICE_BY_ID_SUCCESS = "FETCH_OUTGOING_INVOICE_BY_ID_SUCCESS";
export const FETCH_OUTGOING_INVOICE_BY_ID_ERROR = "FETCH_OUTGOING_INVOICE_BY_ID_ERROR";

/**
 * redux action functions
 */
export function fetchOutgoingInvoiceById( outgoingInvoiceId: Identifier<number> ) : FetchOutgoingInvoiceByIdAction {
    return { type: FETCH_OUTGOING_INVOICE_BY_ID, outgoingInvoiceId };
}
export function fetchOutgoingInvoiceByIdSuccess( payload: OutgoingInvoice) : FetchOutgoingInvoiceByIdSuccessAction {
    return { type: FETCH_OUTGOING_INVOICE_BY_ID_SUCCESS, payload };
}
export function fetchOutgoingInvoiceByIdError(
    outgoingInvoiceId: Identifier< number >,
    error: Error
): FetchOutgoingInvoiceByIdErrorAction {
    return { type: FETCH_OUTGOING_INVOICE_BY_ID_ERROR, error, outgoingInvoiceId };
}

/**
 * redux observable to fetch bills from api
 */
export function fetchOutgoingInvoiceByIdEpic(
    action$: Observable< FetchOutgoingInvoiceByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_OUTGOING_INVOICE_BY_ID),
        switchMap(action => {
            const path = apiPath( action.outgoingInvoiceId.rawValue );

            return ajax(
                APP_CONFIG.getRequestObject( path )
            ).pipe(
                map(response => fetchOutgoingInvoiceByIdSuccess( response.response as OutgoingInvoice)),
                catchError(error => of(
                    fetchOutgoingInvoiceByIdError( action.outgoingInvoiceId, error),
                    eventNotificationError(error, store$, OutgoingInvoiceModule.identifier, "fetchOutgoingInvoiceById")
                ))
            );

        })
    );
}

/**
 * change invoice mode to canceled 
 */
export interface MarkCanceledOutgoingInvoiceAction extends OutgoingInvoiceId, ReduxAction< typeof MARK_CANCELED_OUTGOING_INVOICE > {

}
export interface MarkCanceledOutgoingInvoiceSuccessAction extends ReduxAction<typeof MARK_CANCELED_OUTGOING_INVOICE_SUCCESS > {
}
export interface MarkCanceledOutgoingInvoiceErrorAction extends ErrorAction< typeof MARK_CANCELED_OUTGOING_INVOICE_ERROR >{}

export type MarkCanceledOutgoingInvoiceActionTypes = MarkCanceledOutgoingInvoiceAction | MarkCanceledOutgoingInvoiceErrorAction | MarkCanceledOutgoingInvoiceSuccessAction

/**
 * redux action constants
 */
export const MARK_CANCELED_OUTGOING_INVOICE = "MARK_CANCELED_OUTGOING_INVOICES";
export const MARK_CANCELED_OUTGOING_INVOICE_SUCCESS = "MARK_CANCELED_OUTGOING_INVOICES_SUCCESS";
export const MARK_CANCELED_OUTGOING_INVOICE_ERROR = "MARK_CANCELED_OUTGOING_INVOICES_ERROR";

/**
 * redux action functions
 */
export function markCanceledOutgoingInvoice( outgoingInvoiceId: Identifier< number > ) : MarkCanceledOutgoingInvoiceAction {
    return { type: MARK_CANCELED_OUTGOING_INVOICE, outgoingInvoiceId };
}
export function markCanceledOutgoingInvoiceSuccess() : MarkCanceledOutgoingInvoiceSuccessAction {
    return { type: MARK_CANCELED_OUTGOING_INVOICE_SUCCESS };
}
export function markCanceledOutgoingInvoiceError( error: Error ): MarkCanceledOutgoingInvoiceErrorAction {
    return { type: MARK_CANCELED_OUTGOING_INVOICE_ERROR, error };
}

/**
 * redux observable to fetch bills from api
 */
export function markCanceledOutgoingInvoiceEpic(
    action$: Observable< MarkCanceledOutgoingInvoiceAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( MARK_CANCELED_OUTGOING_INVOICE ),
        switchMap(action => {
            const path = `${ apiPath( action.outgoingInvoiceId.rawValue ) }/canceled`;

            return ajax(
                APP_CONFIG.getRequestObject( path, RouteMethods.patch )
            ).pipe(
                mergeMap(_ => of(
                    markCanceledOutgoingInvoiceSuccess(),
                    fetchOutgoingInvoiceById( action.outgoingInvoiceId ),
                    fetchOutgoingInvoices( getInitialViewSettings( OutgoingInvoiceModule.identifier ) )
                )),
                catchError(error => of(
                    markCanceledOutgoingInvoiceError( error),
                    eventNotificationError(error, store$, OutgoingInvoiceModule.identifier, "markCanceledOutgoingInvoice")
                ))
            );

        })
    );
}

/**
 * change invoice mode to canceled
 */
export interface MarkFinalizedOutgoingInvoiceAction extends OutgoingInvoiceId, ReduxAction< typeof MARK_FINALIZED_OUTGOING_INVOICE > {

}
export interface MarkFinalizedOutgoingInvoiceSuccessAction extends ReduxAction<typeof MARK_FINALIZED_OUTGOING_INVOICE_SUCCESS > {
}
export interface MarkFinalizedOutgoingInvoiceErrorAction extends ErrorAction< typeof MARK_FINALIZED_OUTGOING_INVOICE_ERROR >{}

export type MarkFinalizedOutgoingInvoiceActionTypes = MarkFinalizedOutgoingInvoiceAction | MarkFinalizedOutgoingInvoiceErrorAction | MarkFinalizedOutgoingInvoiceSuccessAction

/**
 * redux action constants
 */
export const MARK_FINALIZED_OUTGOING_INVOICE = "MARK_FINALIZED_OUTGOING_INVOICES";
export const MARK_FINALIZED_OUTGOING_INVOICE_SUCCESS = "MARK_FINALIZED_OUTGOING_INVOICES_SUCCESS";
export const MARK_FINALIZED_OUTGOING_INVOICE_ERROR = "MARK_FINALIZED_OUTGOING_INVOICES_ERROR";

/**
 * redux action functions
 */
export function markFinalizedOutgoingInvoice( outgoingInvoiceId: Identifier< number > ) : MarkFinalizedOutgoingInvoiceAction {
    return { type: MARK_FINALIZED_OUTGOING_INVOICE, outgoingInvoiceId };
}
export function markFinalizedOutgoingInvoiceSuccess() : MarkFinalizedOutgoingInvoiceSuccessAction {
    return { type: MARK_FINALIZED_OUTGOING_INVOICE_SUCCESS };
}
export function markFinalizedOutgoingInvoiceError( error: Error ): MarkFinalizedOutgoingInvoiceErrorAction {
    return { type: MARK_FINALIZED_OUTGOING_INVOICE_ERROR, error };
}

/**
 * redux observable to fetch bills from api
 */
export function markFinalizedOutgoingInvoiceEpic(
    action$: Observable< MarkFinalizedOutgoingInvoiceAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( MARK_FINALIZED_OUTGOING_INVOICE ),
        switchMap(action => {
            const path = `${ apiPath( action.outgoingInvoiceId.rawValue ) }/final`;

            return ajax(
                APP_CONFIG.getRequestObject( path, RouteMethods.patch )
            ).pipe(
                mergeMap(_ => of(
                    markFinalizedOutgoingInvoiceSuccess() ,
                    fetchOutgoingInvoiceById( action.outgoingInvoiceId ),
                    fetchOutgoingInvoices( getInitialViewSettings( OutgoingInvoiceModule.identifier ) )
                )),
                catchError(error => of(
                    markFinalizedOutgoingInvoiceError( error),
                    eventNotificationError(error, store$, OutgoingInvoiceModule.identifier, "markFinalizedOutgoingInvoice")
                ))
            );

        })
    );
}



/**
 * FETCH FILES INVOICE
 */
export interface FetchOutInvoiceFilesByIdAction extends OutgoingInvoiceId, ReduxAction< typeof FETCH_OUTGOING_INVOICE_FILES_BY_ID > {

}
export interface FetchOutInvoiceFilesByIdSuccessAction extends OutgoingInvoiceId, ReduxAction<typeof FETCH_OUTGOING_INVOICE_FILES_BY_ID_SUCCESS > {
    payload: FileData[]

}
export interface FetchOutInvoiceFilesByIdErrorAction extends OutgoingInvoiceId, ErrorAction< typeof FETCH_OUTGOING_INVOICE_FILES_BY_ID_ERROR >{}

export type FetchOutInvoiceFilesByIdActionTypes = FetchOutInvoiceFilesByIdAction | FetchOutInvoiceFilesByIdErrorAction | FetchOutInvoiceFilesByIdSuccessAction

/**
 * redux action constants
 */
export const FETCH_OUTGOING_INVOICE_FILES_BY_ID = "FETCH_OUTGOING_INVOICE_FILES_BY_ID";
export const FETCH_OUTGOING_INVOICE_FILES_BY_ID_SUCCESS = "FETCH_OUTGOING_INVOICE_FILES_BY_ID_SUCCESS";
export const FETCH_OUTGOING_INVOICE_FILES_BY_ID_ERROR = "FETCH_OUTGOING_INVOICE_FILES_BY_ID_ERROR";

/**
 * redux action functions
 */
export function fetchOutInvoiceFilesById( outgoingInvoiceId: Identifier<number> ) : FetchOutInvoiceFilesByIdAction {
    return { type: FETCH_OUTGOING_INVOICE_FILES_BY_ID, outgoingInvoiceId };
}
export function fetchOutInvoiceFilesByIdSuccess( payload: FileData[], outgoingInvoiceId: Identifier<number> ) : FetchOutInvoiceFilesByIdSuccessAction {
    return { type: FETCH_OUTGOING_INVOICE_FILES_BY_ID_SUCCESS, payload, outgoingInvoiceId };
}
export function fetchOutInvoiceFilesByIdError(
    outgoingInvoiceId: Identifier< number >,
    error: Error
): FetchOutInvoiceFilesByIdErrorAction {
    return { type: FETCH_OUTGOING_INVOICE_FILES_BY_ID_ERROR, error, outgoingInvoiceId };
}

/**
 * redux observable to fetch bills from api
 */
export function fetchOutInvoiceFilesByIdEpic(
    action$: Observable< FetchOutInvoiceFilesByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_OUTGOING_INVOICE_FILES_BY_ID),
        switchMap(action => {
            const path = apiPath( action.outgoingInvoiceId.rawValue )+"/files";

            return ajax(
                APP_CONFIG.getRequestObject( path )
            ).pipe(
                map(response => fetchOutInvoiceFilesByIdSuccess( response.response as FileData[], action.outgoingInvoiceId )),
                catchError(error => of(
                    fetchOutInvoiceFilesByIdError( action.outgoingInvoiceId, error),
                    eventNotificationError(error, store$, OutgoingInvoiceModule.identifier, "fetchOutInvoiceFilesById")
                ))
            );

        })
    );
}


/**
 * DELETE IN PROCESS INVOICE
 */
export interface DeleteOutgoingInvoiceByIdAction extends OutgoingInvoiceId, ReduxAction< typeof DELETE_OUTGOING_INVOICE_BY_ID > {

}
export interface DeleteOutgoingInvoiceByIdSuccessAction extends ReduxAction<typeof DELETE_OUTGOING_INVOICE_BY_ID_SUCCESS > {}
export interface DeleteOutgoingInvoiceByIdErrorAction extends OutgoingInvoiceId, ErrorAction< typeof DELETE_OUTGOING_INVOICE_BY_ID_ERROR >{}

export type DeleteOutgoingInvoiceByIdActionTypes = DeleteOutgoingInvoiceByIdAction | DeleteOutgoingInvoiceByIdErrorAction | DeleteOutgoingInvoiceByIdSuccessAction

/**
 * redux action constants
 */
export const DELETE_OUTGOING_INVOICE_BY_ID = "DELETE_OUTGOING_INVOICE_BY_ID";
export const DELETE_OUTGOING_INVOICE_BY_ID_SUCCESS = "DELETE_OUTGOING_INVOICE_BY_ID_SUCCESS";
export const DELETE_OUTGOING_INVOICE_BY_ID_ERROR = "DELETE_OUTGOING_INVOICE_BY_ID_ERROR";

/**
 * redux action functions
 */
export function deleteOutgoingInvoiceById( outgoingInvoiceId: Identifier<number> ) : DeleteOutgoingInvoiceByIdAction {
    return { type: DELETE_OUTGOING_INVOICE_BY_ID, outgoingInvoiceId };
}
export function deleteOutgoingInvoiceByIdSuccess() : DeleteOutgoingInvoiceByIdSuccessAction {
    return { type: DELETE_OUTGOING_INVOICE_BY_ID_SUCCESS };
}
export function deleteOutgoingInvoiceByIdError(
    outgoingInvoiceId: Identifier< number >,
    error: Error
): DeleteOutgoingInvoiceByIdErrorAction {
    return { type: DELETE_OUTGOING_INVOICE_BY_ID_ERROR, error, outgoingInvoiceId };
}

/**
 * redux observable to delete bills from api
 */
export function deleteOutgoingInvoiceByIdEpic(
    action$: Observable< DeleteOutgoingInvoiceByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(DELETE_OUTGOING_INVOICE_BY_ID),
        switchMap(action => {
            const path = apiPath( action.outgoingInvoiceId.rawValue );

            return ajax(
                APP_CONFIG.getRequestObject( path, RouteMethods.delete )
            ).pipe(
                mergeMap(() => of(
                    deleteOutgoingInvoiceByIdSuccess(),
                    fetchOutgoingInvoices( getInitialViewSettings( OutgoingInvoiceModule.identifier ) ),
                        sendUserToPage(
                            RouteConfig.accountBook.routes.main.path,
                            OutgoingInvoiceModule.identifier
                        )
                )),
                catchError(error => of(
                    deleteOutgoingInvoiceByIdError( action.outgoingInvoiceId, error),
                    eventNotificationError(error, store$, OutgoingInvoiceModule.identifier, "deleteOutgoingInvoiceById")
                ))
            );

        })
    );
}
